import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { MultiSegmentedControl } from 'cng-common/components/segmentedControl'
import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import {
  BaseTemplate,
  IBaseTemplate,
  MultiSelectDynamic,
} from 'core-components/library/multiSelect'
import { useStaticCrud } from 'core-components/types/helpers'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../../../configs/translations'
import {
  getCockpitAmplifyMembershipOptions,
  type ICockpitAmplifyMembershipOption,
} from '../../../../../../lib'
import { setCockpitFilterAmplifyMemberships } from '../../../../../../model'
import {
  selectCockpitFilterAmplifyMemberships,
  selectCockpitPartnerData,
  selectCockpitRtm,
} from '../../../../../../model/selectors'
import { CockpitSegmentedControlAccent } from '../../../../../components/cockpitSegmentedControlAccent'
import { CockpitFilterLabel } from '../../../cockpitFilterLabel/CockpitFilterLabel'

import styles from './CockpitAmplifyMembershipFilter.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.amplifyMembership

export const CockpitAmplifyMembershipFilter = ({ dropdown = false }: { dropdown?: boolean }) => {
  const dispatch = useAppDispatch()
  const t = useSafetyTranslation()
  const partnerData = useSelector(selectCockpitPartnerData)
  const selectedRtm = useSelector(selectCockpitRtm)
  const selectedAmplifyMemberships = useSelector(selectCockpitFilterAmplifyMemberships)

  const rtmFilteredPartnerData = useMemo(
    () =>
      partnerData.filter((partner) => {
        if (selectedRtm === 'resellers') return partner.IsCommercial === 1
        if (selectedRtm === 'distributors') return partner.IsDistributor === 1
        return false
      }),
    [partnerData, selectedRtm]
  )

  const amplifyMembershipOptions = useMemo(
    () =>
      getCockpitAmplifyMembershipOptions(
        rtmFilteredPartnerData.map((partner) => partner.AmplifyTrackWithMembership)
      ),
    [rtmFilteredPartnerData]
  )

  const handleSelect = useCallback(
    (memberships: string[]) => {
      dispatch(setCockpitFilterAmplifyMemberships(memberships))
    },
    [dispatch]
  )

  useEffect(() => {
    const validSelections = selectedAmplifyMemberships.filter((membership) =>
      amplifyMembershipOptions.some((option) => option.membership === membership)
    )
    if (validSelections.length !== selectedAmplifyMemberships.length) {
      dispatch(setCockpitFilterAmplifyMemberships(validSelections))
    }
  }, [amplifyMembershipOptions, selectedAmplifyMemberships, dispatch])

  const source = useStaticCrud<ICockpitAmplifyMembershipOption>({
    data: amplifyMembershipOptions,
    keyField: 'membership',
    displayField: 'label',
  })
  const labelByMembership = useMemo(
    () => new Map(amplifyMembershipOptions.map(({ membership, label }) => [membership, label])),
    [amplifyMembershipOptions]
  )
  const renderSelectedAmplifyMembership = useCallback(
    (props: IBaseTemplate<(typeof amplifyMembershipOptions)[number]>) => (
      <BaseTemplate
        {...props}
        className={styles.chip}
        customValueRender={labelByMembership.get(props.item.membership) ?? props.item.label}
      />
    ),
    [labelByMembership]
  )

  if (dropdown) {
    return (
      <div className={styles.root}>
        <CockpitFilterLabel text={KEYS.label} placeholderOnly />
        <MultiSelectDynamic
          source={source}
          caption={'label'}
          value={selectedAmplifyMemberships}
          onChange={handleSelect}
          placeholder={t(KEYS.label)}
          closeOnSelect={false}
          classes={{ inputClasses: { leftSide: styles['chips-line'] } }}
          itemTemplate={renderSelectedAmplifyMembership}
        />
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <Translation text={KEYS.label} span />
      <CockpitSegmentedControlAccent>
        <MultiSegmentedControl
          useItemKeys
          selectedItems={selectedAmplifyMemberships}
          onMultiItemSelect={handleSelect}
          size={'small'}
          variant={'veneer'}
          backgroudn={'bg-white'}
        >
          {amplifyMembershipOptions.map(({ membership, label }) => (
            <MultiSegmentedControl.Item key={membership} itemKey={membership}>
              <span>{label}</span>
            </MultiSegmentedControl.Item>
          ))}
        </MultiSegmentedControl>
      </CockpitSegmentedControlAccent>
    </div>
  )
}
